import './settings/settings-leaf.css';
import ProviderUsage from './provider-usage.tsx';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { aiApi, companyApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, Checkbox, Select, TextArea, TextInput } from './ui.tsx';
import { aiActionCredits, isAiAction, type AiSettings } from '@shared/ai.ts';
import { PLANS, isPlanId } from '@shared/constants/index.ts';
import { MEDIA_RETENTION_CHOICES, type MagicQuoteSettings } from '@shared/magic-quote.ts';
import { companyHasPlanFeature } from './plan-gate.tsx';

/**
 * Settings → AI assists: the toggles, the month's usage meter, the review
 * link, and the review-reply drafter.
 *
 * The meter follows the effective plan's monthly allowance. Free includes
 * none, so it does not show a credit total the plan does not have. A trial
 * shows the trial plan's allowance (Business). Only Magic Quote carries its
 * own plan gate on top of that. When the deployment has no model key at all
 * the section says so and the switches are withheld: a toggle that can never
 * do anything is a lie.
 */
export default function AiSection() {
  const { t } = useTranslation();
  const { can, company, refresh, impersonatedBy } = useSession();

  const canEdit = can('company:write');
  // The AI switches and media retention are the account holder's decision
  // about their customers' data: an operator signed in from /admin sees them
  // but cannot change them, and the API refuses it too. The review link
  // beside them is ordinary settings and stays editable.
  const supportSession = Boolean(impersonatedBy);
  const canEditAi = canEdit && !supportSession;
  // The drafter spends a paid model action, so it follows the API's own gate:
  // write-capable roles only, never the view-only ones.
  const canDraftReply = can('customers:write');
  const usage = useQuery({ queryKey: ['ai-usage'], queryFn: () => aiApi.usage(), enabled: can('company:read') });

  // Null = not edited; render from the session's company until then.
  const [draft, setDraft] = useState<AiSettings | null>(null);
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);
  const [retention, setRetention] = useState<number | null | undefined>(undefined);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const settings = draft ?? company?.aiSettings ?? null;
  const effectiveReviewUrl = reviewUrl ?? company?.reviewRequestUrl ?? '';
  const effectiveRetention = retention === undefined ? (company?.magicQuoteRetentionDays ?? null) : retention;

  const save = useMutation({
    mutationFn: () =>
      companyApi.update({
        ...(draft ? { aiSettings: draft } : {}),
        ...(reviewUrl !== null ? { reviewRequestUrl: reviewUrl.trim() || null } : {}),
        ...(retention !== undefined ? { magicQuoteRetentionDays: retention } : {}),
      }),
    onSuccess: async () => {
      setMessage({ tone: 'success', text: t('settings.aiSaved') });
      setDraft(null);
      setReviewUrl(null);
      setRetention(undefined);
      await refresh();
      void usage.refetch();
    },
    onError: (caught) => {
      setMessage({
        tone: 'error',
        text: caught instanceof ApiRequestError ? caught.message : t('settings.aiSaveFailed'),
      });
    },
  });

  // The review-reply drafter.
  const [reviewText, setReviewText] = useState('');
  const [reply, setReply] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const drafter = useMutation({
    mutationFn: () => aiApi.reviewReply({ reviewText: reviewText.trim() }),
    onSuccess: (result) => {
      setReply(result.reply);
      setCopied(false);
      void usage.refetch();
    },
    onError: (caught) => {
      setMessage({
        tone: 'error',
        text: caught instanceof ApiRequestError ? caught.message : t('settings.aiReplyFailed'),
      });
    },
  });

  if (!company) return null;

  const available = usage.data?.available ?? false;

  function toggle(key: keyof AiSettings, value: boolean) {
    if (!settings) return;
    setDraft({ ...(draft ?? settings), [key]: value });
    setMessage(null);
  }

  /** Magic Quote's own switches live one level down, so they merge one level down. */
  function toggleMagicQuote(key: keyof MagicQuoteSettings, value: boolean) {
    if (!settings) return;
    setDraft({ ...(draft ?? settings), magicQuote: { ...settings.magicQuote, [key]: value } });
    setMessage(null);
  }

  const toggles: Array<{ key: keyof AiSettings; sends: boolean }> = [
    { key: 'leadDrafts', sends: false },
    { key: 'photoAssessments', sends: false },
    { key: 'quickQuotes', sends: false },
    { key: 'smsIntents', sends: false },
    { key: 'tuningSuggestions', sends: false },
    { key: 'crewBriefings', sends: false },
    { key: 'weeklyDigest', sends: true },
    { key: 'followUpCopy', sends: true },
    { key: 'reviewRequests', sends: true },
  ];

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="ai-heading">
      <div className="work-section-heading">
        <h2 id="ai-heading" className="mb-2 text-lg font-semibold">
          {t('settings.aiHeading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.aiDescription')}</p>
      <ProviderUsage kind="ai" />

      {message ? (
        <div className="mb-4">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}

      {!available ? (
        <Alert tone="info">{t('settings.aiUnavailable')}</Alert>
      ) : (
        <>
          {usage.data && usage.data.allowance <= 0 ? (
            <p className="mb-5 text-sm text-[color:var(--color-muted)]">
              {t('settings.aiUsageNone', {
                plan: isPlanId(company.effectivePlan) ? PLANS[company.effectivePlan].name : company.effectivePlan,
              })}
            </p>
          ) : null}

          {usage.data && usage.data.allowance > 0 ? (
            <div className="mb-5 rounded border border-[color:var(--color-line)] p-3 text-sm">
              <div className="mb-1 flex items-baseline justify-between">
                <span className="font-medium">{t('settings.aiUsageHeading')}</span>
                <span className="text-[color:var(--color-muted)]">
                  {t('settings.aiUsageOf', { used: usage.data.used, allowance: usage.data.allowance })}
                </span>
              </div>
              <div
                className="h-2 overflow-hidden rounded bg-[color:var(--color-line)]"
                role="progressbar"
                aria-valuenow={usage.data.used}
                aria-valuemin={0}
                aria-valuemax={usage.data.allowance}
              >
                <div
                  className="h-full bg-[color:var(--color-brand)]"
                  style={{ width: `${Math.min(100, (usage.data.used / Math.max(1, usage.data.allowance)) * 100)}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-[color:var(--color-muted)]">
                {t('settings.aiUsageHint', {
                  plan: isPlanId(company.effectivePlan) ? PLANS[company.effectivePlan].name : company.effectivePlan,
                  allowance: usage.data.allowance,
                  daily: usage.data.dailyAllowance,
                })}
              </p>

              {/* The breakdown, not just the total. "We used 840 this month"
                  is not actionable; "620 of them were walkthrough captures"
                  is — it points at the one feature whose cost scales with the
                  size of the job. Each row carries its own rate too, because
                  assists no longer cost the same as each other and a row that
                  showed only a total would leave an owner unable to work out
                  which use to cut back on. */}
              {Object.keys(usage.data.byAction).length > 0 ? (
                <ul className="mt-2 flex flex-col gap-0.5 text-xs text-[color:var(--color-muted)]">
                  {Object.entries(usage.data.byAction)
                    .filter(([, used]) => used > 0)
                    .sort(([, a], [, b]) => b - a)
                    .map(([action, used]) => (
                      <li key={action} className="flex justify-between gap-3">
                        <span>
                          {t(`settings.aiAction.${action}`, { defaultValue: action })}
                          {isAiAction(action) ? (
                            <span className="opacity-70">
                              {' '}
                              {t('settings.aiActionRate', { credits: aiActionCredits(action) })}
                            </span>
                          ) : null}
                        </span>
                        <span className="tabular-nums">{used}</span>
                      </li>
                    ))}
                </ul>
              ) : null}

              <p className="mt-2 text-xs text-[color:var(--color-muted)]">{t('settings.aiChargingHint')}</p>
            </div>
          ) : null}

          {settings ? (
            <>
              {supportSession && canEdit ? (
                <div className="mb-3">
                  <Alert tone="info">{t('settings.aiSupportSession')}</Alert>
                </div>
              ) : null}
              <Checkbox
                label={t('settings.aiEnabled')}
                hint={t('settings.aiEnabledHint')}
                checked={settings.enabled}
                disabled={!canEditAi}
                onChange={(event) => toggle('enabled', event.target.checked)}
              />

              {settings.enabled ? (
                <div className="mt-2 border-l-2 border-[color:var(--color-line)] pl-4">
                  {toggles.map(({ key, sends }) => (
                    <Checkbox
                      key={key}
                      label={t(`settings.ai_${key}`)}
                      hint={[
                        t(`settings.ai_${key}Hint`),
                        key === 'quickQuotes' && !companyHasPlanFeature(company, 'quick_quote')
                          ? t('settings.ai_quickQuotesPlanNote')
                          : null,
                        sends ? t('settings.aiSendsHint') : null,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      checked={Boolean(settings[key])}
                      disabled={!canEditAi}
                      onChange={(event) => toggle(key, event.target.checked)}
                    />
                  ))}

                  {/* Magic Quote is the one assist with a policy rather than
                      a switch: it records photographs, video and speech inside
                      customers' homes, and which of those a company permits —
                      and how long any of it is kept — is a decision an owner
                      has to be able to make BEFORE anyone walks into a house
                      with it. */}
                  <div className="mt-3 border-t border-[color:var(--color-line)] pt-3">
                    <Checkbox
                      label={t('settings.ai_magicQuote')}
                      hint={t('settings.ai_magicQuoteHint')}
                      checked={settings.magicQuote.enabled}
                      disabled={!canEditAi}
                      onChange={(event) => toggleMagicQuote('enabled', event.target.checked)}
                    />

                    {settings.magicQuote.enabled ? (
                      <div className="mt-2 border-l-2 border-[color:var(--color-line)] pl-4">
                        <Checkbox
                          label={t('settings.ai_magicQuoteVideo')}
                          hint={t('settings.ai_magicQuoteVideoHint')}
                          checked={settings.magicQuote.video}
                          disabled={!canEditAi}
                          onChange={(event) => toggleMagicQuote('video', event.target.checked)}
                        />
                        <Checkbox
                          label={t('settings.ai_magicQuoteAudio')}
                          hint={t('settings.ai_magicQuoteAudioHint')}
                          checked={settings.magicQuote.audio}
                          disabled={!canEditAi}
                          onChange={(event) => toggleMagicQuote('audio', event.target.checked)}
                        />
                        <Checkbox
                          label={t('settings.ai_magicQuotePricing')}
                          hint={t('settings.ai_magicQuotePricingHint')}
                          checked={settings.magicQuote.pricingSuggestions}
                          disabled={!canEditAi}
                          onChange={(event) => toggleMagicQuote('pricingSuggestions', event.target.checked)}
                        />
                        <Select
                          label={t('settings.ai_magicQuoteRetention')}
                          hint={t('settings.ai_magicQuoteRetentionHint')}
                          value={effectiveRetention === null ? '' : String(effectiveRetention)}
                          disabled={!canEditAi}
                          onChange={(event) => {
                            setRetention(event.target.value === '' ? null : Number(event.target.value));
                            setMessage(null);
                          }}
                          options={[
                            { value: '', label: t('settings.ai_magicQuoteRetentionForever') },
                            ...MEDIA_RETENTION_CHOICES.map((days) => ({
                              value: String(days),
                              label: t('settings.ai_magicQuoteRetentionDays', { count: days }),
                            })),
                          ]}
                        />
                      </div>
                    ) : null}
                  </div>

                  {settings.reviewRequests ? (
                    <TextInput
                      label={t('settings.aiReviewUrl')}
                      hint={t('settings.aiReviewUrlHint')}
                      value={effectiveReviewUrl}
                      disabled={!canEdit}
                      placeholder="https://g.page/r/…"
                      onChange={(event) => {
                        setReviewUrl(event.target.value);
                        setMessage(null);
                      }}
                    />
                  ) : null}
                </div>
              ) : null}

              {canEdit ? (
                <div className="mt-4">
                  <Button
                    variant="primary"
                    busy={save.isPending}
                    disabled={save.isPending || (draft === null && reviewUrl === null && retention === undefined)}
                    onClick={() => save.mutate()}
                  >
                    {t('settings.aiSave')}
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}

          {settings?.enabled && canDraftReply ? (
            <div className="mt-6 border-t border-[color:var(--color-line)] pt-4">
              <h3 className="mb-1 text-sm font-semibold">{t('settings.aiReplyHeading')}</h3>
              <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('settings.aiReplyHint')}</p>
              <TextArea
                label={t('settings.aiReplyReview')}
                rows={4}
                value={reviewText}
                onChange={(event) => setReviewText(event.target.value)}
              />
              <Button
                busy={drafter.isPending}
                disabled={drafter.isPending || reviewText.trim().length === 0}
                onClick={() => drafter.mutate()}
              >
                {t('settings.aiReplyDraft')}
              </Button>
              {reply ? (
                <div className="mt-3 rounded border border-[color:var(--color-line)] p-3 text-sm">
                  <p className="whitespace-pre-wrap">{reply}</p>
                  <Button
                    className="mt-2"
                    onClick={() => {
                      void navigator.clipboard?.writeText(reply).then(() => setCopied(true));
                    }}
                  >
                    {copied ? t('settings.aiReplyCopied') : t('settings.aiReplyCopy')}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
